import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardLayout from "./pages/dashboard/DashboardLayout";
import Overview from "./pages/dashboard/Overview";
import Terminal from "./pages/dashboard/Terminal";
import Analytics from "./pages/dashboard/Analytics";
import News from "./pages/dashboard/News";
import Alerts from "./pages/dashboard/Alerts";
import KYC from "./pages/dashboard/KYC";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminLiveTrades from "./pages/admin/AdminLiveTrades";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminKYC from "./pages/admin/AdminKYC";
import AdminManagement from "./pages/admin/AdminManagement";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminEmailNotifications from "./pages/admin/AdminEmailNotifications";
import AdminDepositAccounts from "./pages/admin/AdminDepositAccounts";
import Referral from "./pages/dashboard/Referral";
import { Portfolio, Deposit, Withdraw, History, Settings } from "./pages/dashboard/DashboardPages";

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#050807] flex items-center justify-center text-[#00e676]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  // Intercept the Google OAuth return (session_id in the URL fragment) BEFORE any
  // protected route runs, so RequireAuth doesn't bounce the user to /login first.
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={<RequireAuth><DashboardLayout /></RequireAuth>}>
        <Route index element={<Overview />} />
        <Route path="trade" element={<Terminal />} />
        <Route path="terminal" element={<Terminal />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="news" element={<News />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="kyc" element={<KYC />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="deposit" element={<Deposit />} />
        <Route path="withdraw" element={<Withdraw />} />
        <Route path="referral" element={<Referral />} />
        <Route path="history" element={<History />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="live-trades" element={<AdminLiveTrades />} />
        <Route path="deposits" element={<AdminTransactions type="deposit" />} />
        <Route path="withdrawals" element={<AdminTransactions type="withdraw" />} />
        <Route path="deposit-methods" element={<AdminDepositAccounts />} />
        <Route path="kyc" element={<AdminKYC />} />
        <Route path="admins" element={<AdminManagement />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="email-alerts" element={<AdminEmailNotifications />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Header />
          <AppRouter />
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
